import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import {
  ArtistSubscriptionFragment,
  BillingInfo,
  PaywallAddPaymentOptionDoc,
  PaywallAddPaymentOptionMutation,
  PaywallGetBillingInfoDoc,
  PaywallGetBillingInfoQuery,
  PaywallRemoveBillingInfoDoc,
  PaywallRemoveBillingInfoMutation,
  PaywallUpdateBillingInfoDoc,
  PaywallUpdateBillingInfoMutation,
} from '@minecraft.graphql-operations';
import { TokenPayload } from '@recurly/recurly-js';
import { captureExceptionEvent } from '@blocs.observability';
import { useTranslation } from '@blocs.i18n';
import { useUserContext } from '@blocs.auth';
import { useGlobalNotification } from '@minecraft.notification-provider';
import { parsePaymentsError } from './helpers/parseApolloError';

export type AddPaymentOptionParams = {
  // Token is only optional if we have already attempted a purchase but need the threeDActionToken
  token?: TokenPayload;
  isBackupPaymentMethod?: boolean;
  isPrimaryPaymentMethod?: boolean;
  threeDSecureActionResultTokenId?: string;
};

export type BillingAddressParams = {
  street1?: string;
  street2?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
};

export type UpdatePaymentOptionParams = {
  // Token is only optional if we have already attempted a purchase but need the threeDActionToken
  token?: TokenPayload;
  billingInfoId: string;
  isBackupPaymentMethod?: boolean;
  isPrimaryPaymentMethod?: boolean;
  threeDSecureActionResultTokenId?: string;
  // Billing address is never stored on our side — it is passed straight through to Recurly.
  address?: BillingAddressParams;
};

export type UseBilling = {
  wallet?: PaywallGetBillingInfoQuery['getBillingInfo'];
  loading?: boolean;
  error?: Error;
  onAddPaymentOption: (T: AddPaymentOptionParams) => Promise<PaywallAddPaymentOptionMutation['addBillingInfo']>;
  onUpdatePaymentOption: (
    T: UpdatePaymentOptionParams
  ) => Promise<PaywallUpdateBillingInfoMutation['updateBillingInfo']>;
  onRemovePaymentOption: (T: {
    billingInfoId: string;
  }) => Promise<PaywallRemoveBillingInfoMutation['removeBillingInfo']>;
  selectedPaymentOption?: PaywallGetBillingInfoQuery['getBillingInfo'][0];
  setSelectedPaymentOption: (billingInfo?: PaywallGetBillingInfoQuery['getBillingInfo'][0]) => void;
  addPaymentLoading?: boolean;
  updatePaymentLoading?: boolean;
  removePaymentLoading?: boolean;
  addPaymentError?: string;
  updatePaymentError?: ApolloError;
  removePaymentError?: ApolloError;
  showAddPaymentMethod?: boolean;
  setShowAddPaymentMethod: (T: boolean) => void;
  setVisibleError: (value: string) => void;
};

type UseBillingProps = {
  currentActiveSubscription: ArtistSubscriptionFragment;
};

export const useBilling = ({ currentActiveSubscription }: UseBillingProps): UseBilling => {
  const { t } = useTranslation();
  const { showMessage } = useGlobalNotification();
  const { artistId } = useUserContext();
  const [showAddPaymentMethod, setShowAddPaymentMethod] = React.useState(false);

  const { data, loading, error, client } = useQuery(PaywallGetBillingInfoDoc, {
    variables: { input: {} },
    skip: !artistId,
  });

  const [savePaymentOption, { loading: addPaymentLoading, error: addPaymentError }] =
    useMutation(PaywallAddPaymentOptionDoc);
  const [removePaymentOption, { loading: removePaymentLoading, error: removePaymentError }] =
    useMutation(PaywallRemoveBillingInfoDoc);
  const [updatePaymentOption, { loading: updatePaymentLoading, error: updatePaymentError }] =
    useMutation(PaywallUpdateBillingInfoDoc);

  const [selectedPaymentOptionId, setSelectedPaymentOptionId] = useState<BillingInfo['id']>();
  const [visibleError, setVisibleError] = useState<string>();

  // When we initially get all the data, we select the payment option that may be present on any known subscriptions
  // If not we just select the primary
  const initialSelectedPaymentOption = useMemo(() => {
    if (data && !loading && currentActiveSubscription) {
      let currentPayment = data.getBillingInfo.find(
        (paymentOption) => paymentOption.id === currentActiveSubscription.billingInfoId
      );

      if (!currentPayment) {
        currentPayment = data.getBillingInfo.find((paymentOption) => paymentOption.isPrimaryPaymentMethod);
      }

      return currentPayment;
    }

    return undefined;
  }, [data, loading, currentActiveSubscription]);

  const setSelectedPaymentOption = useCallback((selectedBillingInfo?: BillingInfo) => {
    setSelectedPaymentOptionId(selectedBillingInfo?.id);
  }, []);

  const selectedPaymentOption = useMemo(
    () =>
      (selectedPaymentOptionId != null
        ? data?.getBillingInfo.find((paymentOption) => paymentOption.id === selectedPaymentOptionId)
        : undefined) ?? initialSelectedPaymentOption,
    [data?.getBillingInfo, initialSelectedPaymentOption, selectedPaymentOptionId]
  );

  const updateBillingInfoCache = useCallback(
    ({ newData, deleteId }: { newData?: BillingInfo; deleteId?: string }) => {
      if (deleteId) {
        return client.writeQuery({
          query: PaywallGetBillingInfoDoc,
          data: {
            getBillingInfo: data.getBillingInfo.filter((billingInfo) => billingInfo.id !== deleteId),
          },
          variables: { input: {} },
        });
      }

      let updatedBillingInfoData: BillingInfo[] = data.getBillingInfo;
      const isNewBillingInfoPrimary = newData?.isPrimaryPaymentMethod;
      const isNewBillingInfoBackup = newData?.isBackupPaymentMethod;

      if (isNewBillingInfoPrimary) {
        updatedBillingInfoData = updatedBillingInfoData.map((billingInfo) => {
          if (billingInfo.isPrimaryPaymentMethod) {
            return { ...billingInfo, isPrimaryPaymentMethod: false };
          }
          return billingInfo;
        });
      }

      if (isNewBillingInfoBackup) {
        updatedBillingInfoData = updatedBillingInfoData.map((billingInfo) => {
          if (billingInfo.isBackupPaymentMethod) {
            return { ...billingInfo, isBackupPaymentMethod: false };
          }
          return billingInfo;
        });
      }

      updatedBillingInfoData = [
        ...updatedBillingInfoData.filter((billingInfo) => billingInfo.id !== newData?.id),
        newData,
      ];

      return client.writeQuery({
        query: PaywallGetBillingInfoDoc,
        data: { getBillingInfo: updatedBillingInfoData },
        variables: { input: {} },
      });
    },
    [client, data?.getBillingInfo]
  );

  const onAddPaymentOption = useCallback(
    async ({ token, isBackupPaymentMethod, isPrimaryPaymentMethod, threeDSecureActionResultTokenId }) => {
      setVisibleError(undefined);
      const savePaymentInput = {
        tokenId: token?.id,
        backupPaymentMethod: isBackupPaymentMethod,
        primaryPaymentMethod: isPrimaryPaymentMethod,
        threeDSecureActionResultTokenId,
      };
      // TokenId is not sent when we are in the 3D secure flow, so just to
      // be sure we've had the token previously we do a check here:
      if (!savePaymentInput.tokenId) {
        console.error('No token id found');
        captureExceptionEvent(new Error('No token id found'));
        return undefined;
      }

      const response = await savePaymentOption({
        variables: { input: savePaymentInput },
      });

      if (response?.errors?.length) {
        throw response.errors[0];
      }

      updateBillingInfoCache({ newData: response?.data?.addBillingInfo });

      // When we are done, we show the wallet
      setShowAddPaymentMethod(false);
      showMessage({
        message: t('common:billing.card.added.success'),
        color: 'success',
      });
      setSelectedPaymentOption(response?.data?.addBillingInfo);
      return response?.data?.addBillingInfo;
    },
    [savePaymentOption, updateBillingInfoCache, showMessage, t, setSelectedPaymentOption]
  );

  const onUpdatePaymentOption = useCallback(
    async ({
      token,
      billingInfoId,
      isBackupPaymentMethod,
      isPrimaryPaymentMethod,
      threeDSecureActionResultTokenId,
      address,
    }) => {
      setVisibleError(undefined);
      const updatePaymentInput = {
        billingInfoId,
        tokenId: token?.id,
        backupPaymentMethod: isBackupPaymentMethod,
        primaryPaymentMethod: isPrimaryPaymentMethod,
        threeDSecureActionResultTokenId,
        // Billing address is only forwarded to Recurly when the caller explicitly edited it.
        ...(address && { address }),
      };
      if (!updatePaymentInput.billingInfoId) throw Error('No billing info id found');

      const response = await updatePaymentOption({
        variables: { input: updatePaymentInput },
      });

      if (response?.errors?.length) {
        throw response.errors[0];
      }

      updateBillingInfoCache({ newData: response?.data?.updateBillingInfo });

      // When we are done, we show the wallet
      setShowAddPaymentMethod(false);
      setSelectedPaymentOption(response?.data?.updateBillingInfo);
      showMessage({
        message: t('common:billing.card.added.success'),
        color: 'success',
      });
      return response?.data?.updateBillingInfo;
    },
    [updateBillingInfoCache, updatePaymentOption, showMessage, t, setSelectedPaymentOption]
  );

  const onRemovePaymentOption = useCallback(
    async ({ billingInfoId }) => {
      try {
        const result = await removePaymentOption({ variables: { input: { billingInfoId } } });

        if (result?.errors?.length) {
          throw result.errors[0];
        }

        updateBillingInfoCache({ deleteId: billingInfoId });
        return result?.data?.removeBillingInfo;
      } catch (e) {
        captureExceptionEvent(e, { location: 'useBilling > removeBillingInfo' });
        showMessage({
          message: t('common:billing.card.added.failure'),
          color: 'danger',
        });
        return undefined;
      }
    },
    [removePaymentOption, showMessage, t, updateBillingInfoCache]
  );

  useEffect(() => {
    // Hide the add billing screen if we have a wallet length (at first)
    if (!loading && data?.getBillingInfo) {
      setShowAddPaymentMethod(!data?.getBillingInfo.length);
    }
  }, [loading, data?.getBillingInfo]);

  useEffect(() => {
    // Safety net for unknown errors
    if (addPaymentError) {
      // CICN-later Add all of the possible exception types here:

      const paymentsError = parsePaymentsError(addPaymentError);
      setVisibleError(t(`common:paywall.errors.${paymentsError}`) as string);
    }
  }, [addPaymentError, t]);

  useEffect(() => {
    if (!showAddPaymentMethod) {
      setVisibleError('');
    }
  }, [showAddPaymentMethod]);

  return useMemo(
    () => ({
      onAddPaymentOption,
      addPaymentError: visibleError,
      addPaymentLoading,
      error,
      loading,
      updatePaymentLoading,
      onUpdatePaymentOption,
      updatePaymentOption,
      updatePaymentError,
      onRemovePaymentOption,
      removePaymentLoading,
      removePaymentError,
      selectedPaymentOption,
      setSelectedPaymentOption,
      setShowAddPaymentMethod,
      showAddPaymentMethod,
      wallet: data?.getBillingInfo,
      setVisibleError,
    }),
    [
      onAddPaymentOption,
      visibleError,
      addPaymentLoading,
      error,
      loading,
      updatePaymentLoading,
      onUpdatePaymentOption,
      updatePaymentOption,
      updatePaymentError,
      onRemovePaymentOption,
      removePaymentLoading,
      removePaymentError,
      selectedPaymentOption,
      setSelectedPaymentOption,
      showAddPaymentMethod,
      data?.getBillingInfo,
      setVisibleError,
    ]
  );
};
